import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiErrorMessage } from "../../api/client";
import { usePersona } from "../../hooks/usePersona";
import { roleLabel } from "../../lib/roleLabel";
import { formatWhen } from "../../lib/when";
import { ReportRecipients } from "../../components/ReportRecipients";

/**
 * Agent telemetry — what each decision has earned, and what it has not.
 *
 * The deck's promise is that autonomy is earned per decision and paced by
 * reversibility, not by a model's benchmark scores. This page is that promise
 * made checkable: three stages per decision, the record behind each one
 * counted from the run rows, and a readiness line that says what is still
 * missing rather than offering a score somebody can round up.
 *
 * Moving a stage is a supervisor's decision, it needs a reason, and it is
 * recorded. A decision that is irreversible once sent cannot be promoted at
 * all — the control is not merely disabled, the server refuses it.
 */

const STAGES = ["suggest", "approve", "autonomous"] as const;
type Stage = (typeof STAGES)[number];

const STAGE_WORDS: Record<Stage, { title: string; blurb: string }> = {
  suggest: { title: "Suggest", blurb: "The system proposes. A person acts." },
  approve: { title: "Approve", blurb: "The system acts on sign-off." },
  autonomous: { title: "Autonomous", blurb: "The system acts. Audits are sampled." },
};

interface Tally {
  runs: number;
  auto_completed: number;
  escalated: number;
  blocked_pre: number;
  failed: number;
  approved: number;
  rejected: number;
  needs_more_info: number;
  thumbs_up: number;
  thumbs_down: number;
  last_run_at: string | null;
}

interface Decision {
  key: string;
  label: string;
  why: string;
  reversibility: string;
  approver_role: string;
  never_autonomous: boolean;
  stage: Stage;
  changed_at: string | null;
  tally: Tally;
  readiness: { next: string | null; ready: boolean; missing: string[] };
  recommendation?: {
    action: "promote" | "demote" | "hold";
    to: string | null;
    because: string[];
  };
}

interface Agent {
  hand_key: string;
  name: string;
  decisions: Decision[];
}

function StageTrack({ decision }: { decision: Decision }) {
  const at = STAGES.indexOf(decision.stage);
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
      {STAGES.map((s, i) => {
        const here = i === at;
        const past = i < at;
        const barred = s === "autonomous" && decision.never_autonomous;
        return (
          <div
            key={s}
            data-testid={`stage-${decision.key}-${s}`}
            style={{
              flex: "1 1 150px",
              border: `1px solid ${here ? "var(--accent)" : "var(--border)"}`,
              background: here ? "var(--surface-2)" : "transparent",
              borderRadius: 8,
              padding: "8px 10px",
              opacity: barred ? 0.45 : past ? 0.75 : 1,
            }}
          >
            <div style={{ letterSpacing: "0.1em", textTransform: "uppercase", color: here ? "var(--accent)" : "var(--text-3)" }}>
              {STAGE_WORDS[s].title}
              {here ? " · now" : ""}
            </div>
            <div style={{ marginTop: 2 }}>{STAGE_WORDS[s].blurb}</div>
            {barred ? (
              <div style={{ marginTop: 4, color: "var(--text-3)" }}>
                Not available for this decision.
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function MoveStage({
  agent,
  decision,
  workspaceId,
  prefill,
}: {
  agent: Agent;
  decision: Decision;
  workspaceId: string;
  /** Set by accepting a recommendation: the stage, and the evidence for it. */
  prefill?: { to: string; why: string } | null;
}) {
  const qc = useQueryClient();
  const [to, setTo] = useState<string>("");
  const [why, setWhy] = useState("");

  // Accepting a recommendation fills the control in rather than submitting
  // it. The move is still a person's, and the reason is still theirs to
  // change — it just starts as the evidence that prompted it.
  useEffect(() => {
    if (prefill) {
      setTo(prefill.to);
      setWhy(prefill.why);
    }
  }, [prefill]);
  const move = useMutation({
    mutationFn: () =>
      apiPost(`/api/workspaces/${workspaceId}/autonomy/stage`, {
        hand_key: agent.hand_key,
        decision_key: decision.key,
        stage: to,
        reason: why,
      }),
    onSuccess: () => {
      setTo("");
      setWhy("");
      void qc.invalidateQueries({ queryKey: ["autonomy", workspaceId] });
    },
  });
  const options = STAGES.filter(
    (s) => s !== decision.stage && !(s === "autonomous" && decision.never_autonomous),
  );
  if (options.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
      <select
        aria-label={`Move ${decision.label}`}
        value={to}
        onChange={(e) => setTo(e.target.value)}
      >
        <option value="">Move this decision…</option>
        {options.map((s) => (
          <option key={s} value={s}>
            {STAGE_WORDS[s].title}
          </option>
        ))}
      </select>
      <textarea
        aria-label={`Why ${decision.label}`}
        placeholder="Why — this is recorded against the decision. At least a sentence."
        value={why}
        onChange={(e) => setWhy(e.target.value)}
        rows={2} style={{ flex: "1 1 280px", minWidth: 220 }}
      />
      <button
        type="button"
        className="btn"
        data-testid={`move-${decision.key}`}
        disabled={move.isPending || !to || why.trim().length < 10}
        onClick={() => move.mutate()}
      >
        {move.isPending ? "Recording…" : "Record the change"}
      </button>
      {move.isError ? (
        <span className="pill red">{apiErrorMessage(move.error)}</span>
      ) : null}
    </div>
  );
}

function DecisionCard({
  agent,
  decision,
  workspaceId,
  canEdit,
}: {
  agent: Agent;
  decision: Decision;
  workspaceId: string;
  canEdit: boolean;
}) {
  const t = decision.tally;
  const rec = decision.recommendation;
  const [prefill, setPrefill] = useState<{ to: string; why: string } | null>(null);
  const decided = t.approved + t.rejected + t.needs_more_info;
  return (
    <div className="play-card" data-testid={`decision-${decision.key}`}>
      <div className="play-head">
        <div>
          <div className="play-arch">{decision.label}</div>
          <div className="play-title">{STAGE_WORDS[decision.stage].title}</div>
        </div>
        <div className="play-status">
          <span className={`pill ${decision.reversibility === "irreversible" ? "amber" : "blue"}`}>
            {decision.reversibility === "irreversible" ? "irreversible" : "reversible"}
          </span>
        </div>
      </div>
      <div className="play-meta">
        <span>{decision.why}</span>
      </div>
      <StageTrack decision={decision} />
      <div className="play-meta" style={{ marginTop: 10 }}>
        <span>
          Runs <span className="v">{t.runs}</span>
        </span>
        <span>
          Unattended <span className="v">{t.auto_completed}</span>
        </span>
        <span>
          To a person <span className="v">{t.escalated + t.blocked_pre}</span>
        </span>
        <span>
          Failed <span className="v">{t.failed}</span>
        </span>
        <span>
          Decisions <span className="v">{decided}</span>
        </span>
        <span>
          Approved <span className="v">{t.approved}</span>
        </span>
        <span>
          Readers: right <span className="v">{t.thumbs_up}</span>
        </span>
        <span>
          Readers: wrong <span className="v">{t.thumbs_down}</span>
        </span>
        <span>
          Signed off by <span className="v">{roleLabel(decision.approver_role)}</span>
        </span>
        {t.last_run_at ? (
          <span>
            Last run <span className="v">{formatWhen(t.last_run_at)}</span>
          </span>
        ) : null}
      </div>
      <div className="play-meta" style={{ marginTop: 6 }} data-testid={`readiness-${decision.key}`}>
        {decision.readiness.next === null ? (
          <span>
            {decision.never_autonomous
              ? decision.readiness.missing[0]
              : "This decision is already autonomous; audits are sampled."}
          </span>
        ) : decision.readiness.ready ? (
          <span className="pill green">
            Ready for {STAGE_WORDS[decision.readiness.next as Stage].title.toLowerCase()}
          </span>
        ) : (
          <span>
            Not yet {STAGE_WORDS[decision.readiness.next as Stage].title.toLowerCase()}:{" "}
            {decision.readiness.missing.join("; ")}.
          </span>
        )}
      </div>
      {rec && rec.action !== "hold" ? (
        <div
          className="play-meta"
          style={{ marginTop: 10 }}
          data-testid={`recommendation-${decision.key}`}
        >
          <span className={`pill ${rec.action === "promote" ? "green" : "amber"}`}>
            {rec.action === "promote" ? "Recommended" : "Recommended: pull back"}
          </span>
          <span>
            {rec.action === "promote"
              ? `The record supports moving this to ${STAGE_WORDS[rec.to as Stage].title.toLowerCase()}: ${rec.because.join(", ")}.`
              : `Move this back to ${STAGE_WORDS[rec.to as Stage].title.toLowerCase()}: ${rec.because.join(", ")}.`}
          </span>
          {canEdit ? (
            <button
              type="button"
              className="btn"
              data-testid={`accept-${decision.key}`}
              onClick={() =>
                setPrefill({
                  to: rec.to!,
                  why:
                    rec.action === "promote"
                      ? `Moved on the record: ${rec.because.join(", ")}.`
                      : `Pulled back on the record: ${rec.because.join(", ")}.`,
                })
              }
            >
              Accept
            </button>
          ) : null}
        </div>
      ) : null}
      {canEdit ? (
        <MoveStage
          agent={agent}
          decision={decision}
          workspaceId={workspaceId}
          prefill={prefill}
        />
      ) : null}
      <ReportRecipients
        workspaceId={workspaceId}
        handKey={agent.hand_key}
        decisionKey={decision.key}
        decisionLabel={decision.label}
        canEdit={canEdit}
      />
    </div>
  );
}

function AutonomyInner() {
  const { workspaceId = "" } = useParams();
  const persona = usePersona(workspaceId);
  const canEdit = persona === "cxo";
  const q = useQuery<{ agents: Agent[] }, Error>({
    queryKey: ["autonomy", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/autonomy`),
  });

  const agents = q.data?.agents ?? [];
  const all = agents.flatMap((a) => a.decisions);
  const autonomous = all.filter((d) => d.stage === "autonomous").length;
  const promote = all.filter((d) => d.recommendation?.action === "promote").length;
  const demote = all.filter((d) => d.recommendation?.action === "demote").length;

  return (
    <div className="main-inner" data-testid="autonomy">
      <div className="greet-row">
        <span className="greet">Autonomy</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">AUTONOMY</div>
        <div className="lede">
          {q.isLoading
            ? "Counting the runs…"
            : `${all.length} decision ${all.length === 1 ? "type" : "types"} across ${agents.length} agents · ${autonomous} running unattended.`}
        </div>
        <div className="lede" data-testid="recommendation-summary" style={{ marginTop: 4 }}>
          {promote === 0 && demote === 0
            ? "Nothing to move today."
            : [
                promote > 0
                  ? `${promote} ${promote === 1 ? "decision has" : "decisions have"} earned more rope`
                  : "",
                demote > 0
                  ? `${demote} should be pulled back`
                  : "",
              ]
                .filter(Boolean)
                .join(" · ") + "."}
        </div>
        <div className="play-meta" style={{ marginTop: 6 }}>
          <span>
            Autonomy is earned per decision and paced by reversibility, not by
            a model's benchmark scores. Every figure here is counted from the
            run records.
          </span>
        </div>
      </div>

      {q.isError ? (
        <div className="play-card" data-testid="autonomy-error">
          <div className="play-title">Couldn't load the telemetry.</div>
          <div className="play-meta">
            <span>{q.error.message}</span>
          </div>
        </div>
      ) : null}

      {agents.map((agent) => (
        <div key={agent.hand_key}>
          <div className="section-h">
            <span className="ttl">{agent.name}</span>
          </div>
          <div className="play-grid">
            {agent.decisions.map((d) => (
              <DecisionCard
                key={d.key}
                agent={agent}
                decision={d}
                workspaceId={workspaceId}
                canEdit={canEdit}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Autonomy() {
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <div className="main-inner" data-testid="autonomy-error">
          <div className="lede">Could not load the telemetry: {error.message}</div>
        </div>
      )}
    >
      <AutonomyInner />
    </ErrorBoundary>
  );
}
